import type { AirshowBoundingBox, AirshowManualAlignment } from "./airshowReviewContract";

export interface AirshowDecodedPart {
  name: string;
  positions: Float32Array;
  indices: Uint8Array | Uint16Array | Uint32Array;
}

type UnknownRow = Record<string, unknown>;

function row(value: unknown, path: string): UnknownRow {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as UnknownRow;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  return value;
}

function integer(value: unknown, path: string): number {
  if (!Number.isInteger(value) || (value as number) < 0) throw new Error(`${path} must be a nonnegative integer`);
  return value as number;
}

function decodeEmbeddedBuffer(value: unknown, declaredLength: unknown): ArrayBuffer {
  if (typeof value !== "string" || !/^data:[^,]*;base64,/.test(value)) {
    throw new Error("glTF buffer must use an embedded base64 data URI");
  }
  const encoded = value.slice(value.indexOf(",") + 1);
  let binary: string;
  try {
    binary = atob(encoded);
  } catch {
    throw new Error("glTF buffer base64 is invalid");
  }
  const expected = integer(declaredLength, "glTF.buffers[0].byteLength");
  if (binary.length !== expected) throw new Error("glTF buffer byteLength does not match its payload bounds");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

interface ParsedBufferView { byteOffset: number; byteLength: number }
interface ParsedAccessor {
  bufferView: number;
  byteOffset: number;
  componentType: number;
  count: number;
  type: string;
}

function parseBufferView(value: unknown, index: number, totalBytes: number): ParsedBufferView {
  const source = row(value, `glTF.bufferViews[${index}]`);
  if (integer(source.buffer, `glTF.bufferViews[${index}].buffer`) !== 0) {
    throw new Error("glTF bufferView references an unsupported buffer");
  }
  const byteOffset = source.byteOffset === undefined ? 0 : integer(source.byteOffset, `glTF.bufferViews[${index}].byteOffset`);
  const byteLength = integer(source.byteLength, `glTF.bufferViews[${index}].byteLength`);
  if (byteOffset + byteLength > totalBytes) throw new Error("glTF bufferView exceeds payload bounds");
  return { byteOffset, byteLength };
}

function parseAccessor(value: unknown, index: number): ParsedAccessor {
  const source = row(value, `glTF.accessors[${index}]`);
  const count = integer(source.count, `glTF.accessors[${index}].count`);
  if (count > 50_000_000) throw new Error("glTF accessor count exceeds the review limit");
  return {
    bufferView: integer(source.bufferView, `glTF.accessors[${index}].bufferView`),
    byteOffset: source.byteOffset === undefined ? 0 : integer(source.byteOffset, `glTF.accessors[${index}].byteOffset`),
    componentType: integer(source.componentType, `glTF.accessors[${index}].componentType`),
    count,
    type: typeof source.type === "string" ? source.type : "",
  };
}

function boundedOffset(
  accessor: ParsedAccessor,
  view: ParsedBufferView,
  elementBytes: number,
  path: string,
): number {
  const required = accessor.count * elementBytes;
  if (accessor.byteOffset + required > view.byteLength) throw new Error(`${path} exceeds bufferView bounds`);
  const offset = view.byteOffset + accessor.byteOffset;
  if (offset % Math.min(elementBytes, 4) !== 0) throw new Error(`${path} is not byte aligned`);
  return offset;
}

function decodePositions(
  buffer: ArrayBuffer,
  accessor: ParsedAccessor,
  views: readonly ParsedBufferView[],
): Float32Array {
  if (accessor.componentType !== 5126 || accessor.type !== "VEC3") {
    throw new Error("glTF POSITION must be a FLOAT VEC3 accessor");
  }
  const view = views[accessor.bufferView];
  if (!view) throw new Error("glTF POSITION references a missing bufferView");
  const offset = boundedOffset(accessor, view, 12, "glTF POSITION");
  return new Float32Array(buffer, offset, accessor.count * 3).slice();
}

function decodeIndices(
  buffer: ArrayBuffer,
  accessor: ParsedAccessor,
  views: readonly ParsedBufferView[],
): Uint8Array | Uint16Array | Uint32Array {
  if (accessor.type !== "SCALAR" || ![5121, 5123, 5125].includes(accessor.componentType)) {
    throw new Error("glTF indices must be an unsigned SCALAR accessor");
  }
  const view = views[accessor.bufferView];
  if (!view) throw new Error("glTF indices reference a missing bufferView");
  const bytes = accessor.componentType === 5121 ? 1 : accessor.componentType === 5123 ? 2 : 4;
  const offset = boundedOffset(accessor, view, bytes, "glTF indices");
  if (accessor.componentType === 5121) return new Uint8Array(buffer, offset, accessor.count).slice();
  if (accessor.componentType === 5123) return new Uint16Array(buffer, offset, accessor.count).slice();
  return new Uint32Array(buffer, offset, accessor.count).slice();
}

export function decodeAirshowGltf(value: unknown): readonly AirshowDecodedPart[] {
  const document = row(value, "glTF");
  const buffers = array(document.buffers, "glTF.buffers");
  if (buffers.length !== 1) throw new Error("Airshow review glTF must contain one embedded buffer");
  const bufferSource = row(buffers[0], "glTF.buffers[0]");
  const buffer = decodeEmbeddedBuffer(bufferSource.uri, bufferSource.byteLength);
  const views = array(document.bufferViews, "glTF.bufferViews")
    .map((view, index) => parseBufferView(view, index, buffer.byteLength));
  const accessors = array(document.accessors, "glTF.accessors").map(parseAccessor);
  const parts: AirshowDecodedPart[] = [];
  array(document.meshes, "glTF.meshes").forEach((meshValue, meshIndex) => {
    const mesh = row(meshValue, `glTF.meshes[${meshIndex}]`);
    const meshName = typeof mesh.name === "string" && mesh.name.trim() ? mesh.name : `component_${meshIndex}`;
    array(mesh.primitives, `glTF.meshes[${meshIndex}].primitives`).forEach((primitiveValue, primitiveIndex) => {
      const primitive = row(primitiveValue, `glTF.meshes[${meshIndex}].primitives[${primitiveIndex}]`);
      const attributes = row(primitive.attributes, `glTF.meshes[${meshIndex}].primitives[${primitiveIndex}].attributes`);
      const positionAccessor = accessors[integer(attributes.POSITION, "glTF primitive POSITION")];
      const indexAccessor = accessors[integer(primitive.indices, "glTF primitive indices")];
      if (!positionAccessor || !indexAccessor) throw new Error("glTF primitive references a missing accessor");
      const positions = decodePositions(buffer, positionAccessor, views);
      const indices = decodeIndices(buffer, indexAccessor, views);
      if ([...indices].some((vertexIndex) => vertexIndex >= positionAccessor.count)) {
        throw new Error("glTF index exceeds POSITION bounds");
      }
      parts.push({
        name: primitiveIndex ? `${meshName} · ${primitiveIndex + 1}` : meshName,
        positions,
        indices,
      });
    });
  });
  if (!parts.length) throw new Error("Airshow review glTF contains no renderable primitives");
  return parts;
}

export function computeBboxAlignment(
  reference: AirshowBoundingBox,
  candidate: AirshowBoundingBox,
): AirshowManualAlignment {
  const referenceSize = reference[0].map((minimum, index) => reference[1][index]! - minimum);
  const candidateSize = candidate[0].map((minimum, index) => candidate[1][index]! - minimum);
  const candidateExtent = Math.max(...candidateSize);
  const scale = candidateExtent > 0 ? Math.max(...referenceSize) / candidateExtent : 1;
  const referenceCenter = reference[0].map((minimum, index) => (minimum + reference[1][index]!) / 2);
  const candidateCenter = candidate[0].map((minimum, index) => (minimum + candidate[1][index]!) / 2);
  return {
    translation: [
      referenceCenter[0]! - candidateCenter[0]! * scale,
      referenceCenter[1]! - candidateCenter[1]! * scale,
      referenceCenter[2]! - candidateCenter[2]! * scale,
    ],
    rotationDegrees: [0, 0, 0],
    scale,
  };
}
